/**
 * Shared constants for governance UI components.
 */

import {
    PolicyEnforcement,
    PolicyStatus,
} from '@experiences/interfaces';

const SIMULATED_COLOR = 'var(--color-warning-text)';

/** Text colors for enforcement results (Allow/Deny/Simulated). */
export const ENFORCEMENT_COLORS: Record<string, string> = {
    Allow: 'var(--color-success-icon)',
    Deny: 'var(--color-error-text)',
    SimulatedAllow: SIMULATED_COLOR,
    SimulatedDeny: SIMULATED_COLOR,
};

export const DEFAULT_ENFORCEMENT_COLOR = 'var(--color-warning-text)';

type TranslateFn = (descriptor: { id: string }) => string;

/** User-friendly labels for enforcement result values. */
export function buildEnforcementDisplayLabels(translate: TranslateFn): Record<string, string> {
    return {
        Allow: translate({ id: 'CLIENT_GOVERNANCE_TRACES_RESULT_ALLOW' }),
        Deny: translate({ id: 'CLIENT_GOVERNANCE_TRACES_RESULT_DENY' }),
        SimulatedAllow: translate({ id: 'CLIENT_GOVERNANCE_TRACES_RESULT_SIMULATED_ALLOW' }),
        SimulatedDeny: translate({ id: 'CLIENT_GOVERNANCE_TRACES_RESULT_SIMULATED_DENY' }),
    };
}

/**
 * Derive the effective enforcement key from a trace item's evaluation result
 * and policy status. Simulated policies return policyEvaluationResult as
 * "Allow"/"Deny" but need to display as "SimulatedAllow"/"SimulatedDeny".
 */
export function deriveEnforcementResult(
    policyEvaluationResult?: string,
    policyStatus?: string,
): string {
    const result = policyEvaluationResult ?? '';
    if (policyStatus === PolicyStatus.Simulated && (result === 'Allow' || result === 'Deny')) {
        return `Simulated${result}`;
    }
    return result;
}

// ─── Translation key sets for rule display ──────────────────────────────────

interface TranslationKeySet {
    excludeAll: Record<string, string>;
    all: Record<string, string>;
    except: Record<string, string>;
    only: Record<string, string>;
}

export const RESOURCE_TRANSLATION_KEYS: TranslationKeySet = {
    excludeAll: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_AGENTIC_PROCESSES',
        RPAWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_RPA_WORKFLOWS',
        APIWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_API_WORKFLOWS',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCLUDE_ALL_FLOWS',
    },
    all: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_AGENTIC_PROCESSES',
        RPAWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_RPA_WORKFLOWS',
        APIWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_API_WORKFLOWS',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ALL_FLOWS',
    },
    except: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_AGENTIC_PROCESSES',
        RPAWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_RPA_WORKFLOWS',
        APIWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_API_WORKFLOWS',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_EXCEPT_FLOWS',
    },
    only: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_AGENTIC_PROCESSES',
        RPAWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_RPA_WORKFLOWS',
        APIWorkflow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_API_WORKFLOWS',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_ONLY_FLOWS',
    },
};

export const AUTOMATION_TRANSLATION_KEYS: TranslationKeySet = {
    excludeAll: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCLUDE_ALL_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCLUDE_ALL_PROCESSES',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCLUDE_ALL_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCLUDE_ALL_FLOWS',
    },
    all: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ALL_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ALL_PROCESSES',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ALL_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ALL_FLOWS',
    },
    except: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCEPT_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCEPT_PROCESSES',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCEPT_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_EXCEPT_FLOWS',
    },
    only: {
        Agent: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ONLY_AGENTS',
        AgenticProcess: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ONLY_PROCESSES',
        CaseManagement: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ONLY_CASE_MANAGEMENT',
        Flow: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_ONLY_FLOWS',
    },
};

export const RESOURCE_TYPE_PLURAL_NAME_KEYS: Record<string, string> = {
    Agent: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_AGENTS',
    AgenticProcess: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_AGENTIC_PROCESSES',
    RPAWorkflow: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_RPA_WORKFLOWS',
    APIWorkflow: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_API_WORKFLOWS',
    CaseManagement: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_CASE_MANAGEMENT',
    Flow: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_FLOWS',
};

export const AUTOMATION_TYPE_PLURAL_NAME_KEYS: Record<string, string> = {
    Agent: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_AGENTS',
    AgenticProcess: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_AGENTIC_PROCESSES',
    CaseManagement: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_CASE_MANAGEMENT',
    Flow: 'CLIENT_GOVERNANCE_RESOURCE_TYPE_FLOWS',
};

export const ACTOR_IDENTITY_TYPE_PLURAL_NAME_KEYS: Record<string, string> = {
    User: 'CLIENT_GOVERNANCE_ACTOR_IDENTITY_TYPE_USERS',
    ExternalApplication: 'CLIENT_GOVERNANCE_ACTOR_IDENTITY_TYPE_EXTERNAL_APPLICATIONS',
    Robot: 'CLIENT_GOVERNANCE_ACTOR_IDENTITY_TYPE_ROBOTS',
    Group: 'CLIENT_GOVERNANCE_ACTOR_IDENTITY_TYPE_GROUPS',
};

export const ACTOR_IDENTITY_TRANSLATION_KEYS: TranslationKeySet = {
    excludeAll: {
        User: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCLUDE_ALL_USERS',
        ExternalApplication: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCLUDE_ALL_EXTERNAL_APPLICATIONS',
        Robot: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCLUDE_ALL_ROBOTS',
        Group: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCLUDE_ALL_GROUPS',
    },
    all: {
        User: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ALL_USERS',
        ExternalApplication: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ALL_EXTERNAL_APPLICATIONS',
        Robot: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ALL_ROBOTS',
        Group: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ALL_GROUPS',
    },
    except: {
        User: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCEPT_USERS',
        ExternalApplication: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCEPT_EXTERNAL_APPLICATIONS',
        Robot: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCEPT_ROBOTS',
        Group: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_EXCEPT_GROUPS',
    },
    only: {
        User: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ONLY_USERS',
        ExternalApplication: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ONLY_EXTERNAL_APPLICATIONS',
        Robot: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ONLY_ROBOTS',
        Group: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACTOR_IDENTITY_ONLY_GROUPS',
    },
};

export const ACCESS_DECISION_KEYS: Record<PolicyEnforcement, string> = {
    [PolicyEnforcement.Allow]: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_ALLOW',
    [PolicyEnforcement.Prompt]: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_ALLOW_WITH_APPROVAL',
    [PolicyEnforcement.Deny]: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_DENY',
    [PolicyEnforcement.NoOp]: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_NO_OPERATION',
};

// ─── Display item helpers ────────────────────────────────────────────────────

export interface DisplayItem {
    text: string;
    values?: string[];
    invalidValueIndices?: number[];
}

/** Resolves a single selector/executable-rule entry into a display item. */
export function resolveDisplayItem(
    entry: { values: string[]; operator: string },
    type: string,
    keys: TranslationKeySet,
    translate: TranslateFn,
    valueNameMap: Record<string, string>,
): DisplayItem | null {
    const isAll = entry.values.length === 1 && entry.values[0] === '*';

    if (isAll) {
        const key = entry.operator === 'None' ? keys.excludeAll[type] : keys.all[type];
        return key ? { text: translate({ id: key }) } : null;
    }

    const key = entry.operator === 'None' ? keys.except[type] : keys.only[type];
    if (!key) {
        return null;
    }

    const invalidIndices: number[] = [];
    const values = entry.values.map((v, idx) => {
        const mapped = valueNameMap[v] ?? v;
        if (mapped === v && !valueNameMap[v]) {
            invalidIndices.push(idx);
        }
        return mapped;
    });

    return {
        text: translate({ id: key }),
        values,
        ...(invalidIndices.length > 0 && { invalidValueIndices: invalidIndices }),
    };
}

/** Computes the chip label for a type entry in the policy definition step. */
export function computeTypeChipLabel(
    isAll: boolean,
    operator: string,
    pluralName: string,
    count: number,
    excludeText: string,
): string {
    if (isAll && operator === 'None') {
        return `${excludeText} ${pluralName}`;
    }
    if (isAll) {
        return pluralName;
    }
    if (operator === 'None') {
        return `${excludeText} ${pluralName} (${count})`;
    }
    return `${pluralName} (${count})`;
}

/** Translates a tag operator string into a short display label. */
export function getTagOperatorShortText(
    tagOperator: string,
    translate: TranslateFn,
): string {
    if (tagOperator === 'And') {
        return translate({ id: 'CLIENT_GOVERNANCE_RESOURCE_RULE_TAG_LOGIC_ALL_SHORT' });
    }
    if (tagOperator === 'None') {
        return translate({ id: 'CLIENT_GOVERNANCE_RESOURCE_RULE_TAG_LOGIC_NONE_SHORT' });
    }
    return translate({ id: 'CLIENT_GOVERNANCE_RESOURCE_RULE_TAG_LOGIC_ANY_SHORT' });
}
